import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import useIsMobile from "../../hooks/useIsMobile";
import {
  dayIn, formatDayHeader, formatTime, formatWeek, missionErrorMessage, planningByDay, planningRows, shiftWeek, weekOf,
} from "../../lib/missions";
import { MissionStatusBadge } from "./MissionBits";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, IdCard, RefreshCw, User } from "lucide-react";

// Planning: one week of the missions the person may see (cancelled ones excluded by the server), one row per
// person of the teams. Two scheduled missions of the same person at overlapping times are flagged, never blocked.
export default function MissionPlanning() {
  const mobile = useIsMobile();
  const today = dayIn(new Date().toISOString());
  // ?week=AAAA-MM-JJ (any day of the week) keeps the week in the address, so it can be shared or reopened.
  const [searchParams, setSearchParams] = useSearchParams();
  const asked = searchParams.get("week");
  const days = useMemo(() => weekOf(/^\d{4}-\d{2}-\d{2}$/.test(asked || "") ? asked : today), [asked, today]);
  const setDays = (update) => {
    const next = typeof update === "function" ? update(days) : update;
    setSearchParams((p) => { const out = new URLSearchParams(p); out.set("week", next[0]); return out; }, { replace: true });
  };
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data } = await api.get("/missions/planning", { params: { from: days[0], to: days[6] } });
      if (mine !== seq.current) return;
      setItems(data.items);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(missionErrorMessage(e, "Impossible de charger le planning."));
      setPhase("error");
    }
  }, [days]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const rows = useMemo(() => planningRows(items, days), [items, days]);
  const byDay = useMemo(() => planningByDay(items, days), [items, days]);
  const clashCount = rows.reduce((n, r) => n + r.clashes.size, 0);

  return (
    <div className="space-y-3" data-testid="missions-planning">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white">
          <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Semaine précédente" onClick={() => setDays((d) => shiftWeek(d, -1))} data-testid="planning-prev"><ChevronLeft size={16} /></Button>
          <span className="px-2 text-sm font-medium text-slate-900 whitespace-nowrap" data-testid="planning-week">{formatWeek(days)}</span>
          <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Semaine suivante" onClick={() => setDays((d) => shiftWeek(d, 1))} data-testid="planning-next"><ChevronRight size={16} /></Button>
        </div>
        {!days.includes(today) && <Button variant="outline" size="sm" className="h-9" onClick={() => setDays(weekOf(today))} data-testid="planning-today">Cette semaine</Button>}
        {phase === "ready" && clashCount > 0 && (
          <span className="inline-flex items-center gap-1.5 text-sm text-amber-800" data-testid="planning-clashes">
            <AlertTriangle size={14} /> Des personnes ont des missions qui se chevauchent
          </span>
        )}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="planning-empty">
          <CalendarDays className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">Aucune mission cette semaine</h3>
          <p className="text-sm text-slate-500">Passez à la semaine suivante, ou consultez la liste des missions.</p>
        </Card>
      )}

      {phase === "ready" && items.length > 0 && !mobile && (
        <Card className="overflow-x-auto" data-testid="planning-grid">
          <table className="w-full min-w-[56rem] table-fixed text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/60">
                <th className="w-44 px-3 py-2 text-left text-xs font-semibold text-slate-500">Personne</th>
                {days.map((d) => (
                  <th key={d} className={`px-2 py-2 text-left text-xs font-semibold ${d === today ? "text-jamora" : "text-slate-500"}`}>{formatDayHeader(d)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-slate-100 align-top" data-testid={`planning-row-${r.key}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {r.kind === "collaborator" ? <IdCard size={14} className="text-slate-400 shrink-0" /> : r.kind === "user" ? <User size={14} className="text-slate-400 shrink-0" /> : null}
                      <span className={`truncate ${r.kind === "none" ? "text-slate-500 italic" : "text-slate-900 font-medium"}`}>{r.name}</span>
                    </div>
                  </td>
                  {days.map((d) => (
                    <td key={d} className={`px-1.5 py-1.5 ${d === today ? "bg-jamora-50/30" : ""}`}>
                      <div className="space-y-1">
                        {r.cells[d].map((m) => <PlanningChip key={m.id} mission={m} clash={r.clashes.has(m.id)} />)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {phase === "ready" && items.length > 0 && mobile && (
        <div className="space-y-3" data-testid="planning-days">
          {byDay.filter((d) => d.missions.length).map(({ day, missions }) => (
            <div key={day}>
              <div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${day === today ? "text-jamora" : "text-slate-500"}`}>{formatDayHeader(day)}</div>
              <Card className="divide-y divide-slate-100 overflow-hidden">
                {missions.map((m) => {
                  const clash = rows.some((r) => r.clashes.has(m.id));
                  return (
                    <Link key={m.id} to={`/missions/${m.id}`} className="block p-3 active:bg-slate-50" data-testid={`planning-day-item-${m.reference}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-slate-900 break-words">{m.title}</span>
                        <MissionStatusBadge status={m.status} className="shrink-0" />
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{formatTime(m.planned_start_at, m.timezone)} → {formatTime(m.planned_end_at, m.timezone)} · {m.team.map((t) => t.member.name).join(", ") || "Sans équipe"}</div>
                      {clash && <div className="text-xs text-amber-800 mt-0.5 inline-flex items-center gap-1"><AlertTriangle size={12} />Chevauchement pour une personne de l'équipe</div>}
                    </Link>
                  );
                })}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlanningChip({ mission, clash }) {
  return (
    <Link to={`/missions/${mission.id}`} title={`${mission.reference} · ${mission.title}`}
      className={`block rounded-md border px-1.5 py-1 text-xs leading-tight hover:shadow-sm ${clash ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"} ${mission.status === "draft" || mission.status === "postponed" ? "border-dashed" : ""}`}
      data-testid={`planning-chip-${mission.reference}${clash ? "-clash" : ""}`}>
      <div className="flex items-center gap-1 text-slate-500">
        {clash && <AlertTriangle size={11} className="text-amber-700 shrink-0" aria-label="Chevauchement" />}
        <span>{formatTime(mission.planned_start_at, mission.timezone)}</span>
      </div>
      <div className="font-medium text-slate-900 truncate">{mission.title}</div>
    </Link>
  );
}
